import axios from 'axios';
import { toast } from 'sonner';
import { logger } from './utils/logger';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// ── Silent refresh (single-flight) ──
// Collapses concurrent 401s into ONE /auth/refresh call. Critical: two
// parallel rotations of the same refresh token would trip server-side
// reuse-detection and revoke the whole token family.
let refreshPromise = null;
function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api.post('/auth/refresh').finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// ── Retry config ──
const RETRY_STATUS = new Set([502, 503, 504]);
const MAX_RETRIES = 2;
const BASE_DELAY = 800; // ms

function shouldRetry(error, attempt) {
  if (attempt >= MAX_RETRIES) return false;
  if (!error.response) return true; // network error / timeout
  return RETRY_STATUS.has(error.response.status);
}

function delay(ms) {
  return new Promise(r => setTimeout(r, ms));
}

// ── Offline awareness ──
let offlineToastShown = false;

function showOfflineToast() {
  if (offlineToastShown) return;
  offlineToastShown = true;
  toast.error('You appear to be offline', { id: 'offline-toast', duration: Infinity });
}

function dismissOfflineToast() {
  if (!offlineToastShown) return;
  offlineToastShown = false;
  toast.dismiss('offline-toast');
  toast.success('Back online', { id: 'online-toast', duration: 2000 });
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', dismissOfflineToast);
  window.addEventListener('offline', showOfflineToast);
}

// ── Mutation broadcast ──
// Any successful POST/PUT/DELETE/PATCH on a data endpoint fires the
// `polkdesq:data-mutated` window event. `useTodayData` + any other view
// that subscribes will revalidate. Skips read-only POSTs (search, audit
// log, AI helpers) and auth — those shouldn't trigger data refresh.
const MUTATION_METHODS = new Set(['post', 'put', 'delete', 'patch']);
const SKIP_PATH_PATTERNS = [
  /^\/?auth(\/|$)/,
  /^\/?audit-log(\/|$)/,
  /^\/?search(\/|$)/,
  /^\/?ai\/rewrite/,
  /^\/?ai\/classify/,
  /^\/?voice(\/|$)/,
  /^\/?daybreak\/skip/,
  /^\/?backup(\/|$)/,
  /^\/?webhooks\/[^/]+\/test/,
];

function shouldBroadcast(config) {
  const method = (config.method || '').toLowerCase();
  if (!MUTATION_METHODS.has(method)) return false;
  // Normalize URL (strip baseURL if present, strip leading slash).
  let url = (config.url || '').replace(/^https?:\/\/[^/]+/, '');
  url = url.replace(/^\/?api\//, '/').replace(/^\//, '');
  return !SKIP_PATH_PATTERNS.some((re) => re.test(url));
}

function emitMutated(config) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('polkdesq:data-mutated', {
      detail: { method: config.method, url: config.url },
    }));
  } catch (err) {
    logger.error('polkdesq:data-mutated dispatch failed:', err);
  }
}

// ── Response interceptor: 401 redirect + retry with backoff ──
api.interceptors.response.use(
  (response) => {
    // If we were offline and now got a response, dismiss toast
    if (offlineToastShown) dismissOfflineToast();
    if (shouldBroadcast(response.config)) emitMutated(response.config);
    return response;
  },
  async (error) => {
    const config = error.config;

    // 401 → attempt ONE silent refresh-token rotation, then retry the
    // original request. If the refresh itself fails, fall back to /login.
    if (error.response?.status === 401) {
      const url = config?.url || '';
      const isAuthEndpoint = /\/?auth\/(refresh|login|register|logout)(\/|$|\?)/.test(url);
      const path = window.location.pathname;
      // Don't auto-redirect from the public landing page (`/`), auth pages,
      // OAuth callback, or share routes — those have their own handling and
      // a 401 from `/auth/me` is expected for unauthenticated visitors.
      const isPublic = path === '/' || path === '/login' || path === '/register' ||
                       path.startsWith('/auth/') || path.startsWith('/share/');

      if (!isAuthEndpoint && config && !config._refreshRetried) {
        config._refreshRetried = true;
        try {
          await refreshSession();
          return api(config); // retry with the freshly-rotated access cookie
        } catch (refreshErr) {
          if (!isPublic) window.location.href = '/login';
          return Promise.reject(error);
        }
      }

      if (!isPublic) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // 409 → optimistic-concurrency conflict (DATA-1): the item was updated
    // elsewhere (another tab/device). Surface a refresh prompt; the response
    // detail carries the latest doc. Never retried.
    if (error.response?.status === 409) {
      const detail = error.response.data?.detail;
      if (detail && detail.latest) {
        toast.error(detail.message || 'This item was updated elsewhere — refresh to see the latest.', { id: 'conflict-toast' });
      }
      return Promise.reject(error);
    }

    // 429 → rate limited, do NOT retry
    if (error.response?.status === 429) {
      return Promise.reject(error);
    }

    // Network error → show offline toast
    if (!error.response && !navigator.onLine) {
      showOfflineToast();
    }

    // Retry logic for transient failures
    config._retryCount = config._retryCount || 0;
    if (shouldRetry(error, config._retryCount)) {
      config._retryCount += 1;
      const backoff = BASE_DELAY * Math.pow(2, config._retryCount - 1);
      await delay(backoff);
      return api(config);
    }

    return Promise.reject(error);
  }
);

export default api;
